import instance from "../services/request";
import { extractGifPoster } from "../components/SegmentBuilder/Shared/SegmentStyling/logic/gifPoster";
// Key construction (slug + per-upload uniqueness) lives in ONE place so every
// upload surface inherits the collision guarantee — see utils/uploadObjectKey.js
// for the defect this fixes. Do not re-derive keys locally.
import { buildUniqueObjectKey } from "./uploadObjectKey";
import { isStorageLimitError } from "./storageLimit";
import { showStorageLimitErrorToast } from "./storageLimitToast";

const API_ENDPOINT = "/api/presigned-url/";

export const getPresignedUrl = async (fileKey, fileSize) => {
  try {
    const response = await instance.post(API_ENDPOINT, {
      object_path: fileKey,
      object_size: fileSize,
    });
    if (response.status === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to get pre-signed URL");
    }
  } catch (error) {
    // ISP-9999 (gordon-0904-over-limit-upload-must-block): the backend now
    // answers 402 { code: "storage_limit_exceeded" } once the account is at or
    // over its plan allocation (Backend#3014). This is the LAST-LINE net, not
    // the primary UX: the named upload surfaces disable their controls up front
    // so the request is never sent. But ~28 call sites reach S3 through this ONE
    // helper, and a stale cached usage read, a second tab, or a surface not yet
    // wired would otherwise surface the refusal as a bare
    // "An error occurred while processing the file" with no reason and no way
    // out. Toasting here gives every one of them the same explanation and the
    // same Upgrade link, for free.
    if (isStorageLimitError(error)) {
      showStorageLimitErrorToast(error);
    } else {
      console.error("Error fetching pre-signed URL:", error);
    }
    throw error;
  }
};

/**
 * ISP-9999 — best-effort GIF poster upload on the presigned path.
 *
 * The Wheel Builder uploads a gif fill/portrait via a direct-to-S3 presigned PUT,
 * so the bytes never reach Django and the server can't extract a poster. When the
 * backend signals GIF-animate is on (it returns `poster_presigned_url` for a `.gif`
 * key), extract the gif's first frame CLIENT-side (gifPoster.js) and PUT it to the
 * `<key>.poster.jpg` the fill/portrait read serializers derive — so the poster FILE
 * actually exists (200, not 403) and the card renders the still at rest / animates
 * on hover. Fully best-effort: any failure (extraction, PUT) is swallowed so a
 * poster can NEVER break the primary gif upload. Returns the poster object URL on
 * success, else null.
 */
const uploadGifPoster = async (file, posterPresignedUrl, posterObjectUrl) => {
  if (!posterPresignedUrl || !file) return null;
  let objectUrl;
  try {
    objectUrl = URL.createObjectURL(file);
    // JPEG to match the signed ContentType (image/jpeg) + the `.poster.jpg` key.
    const posterDataUrl = await extractGifPoster(objectUrl, {
      mime: "image/jpeg",
    });
    if (!posterDataUrl) return null;
    const posterBlob = await (await fetch(posterDataUrl)).blob();
    const posterResponse = await fetch(posterPresignedUrl, {
      method: "PUT",
      body: posterBlob,
      mode: "cors",
      headers: {
        "x-amz-acl": "public-read",
        "Content-Type": "image/jpeg",
      },
    });
    if (!posterResponse.ok) return null;
    return posterObjectUrl || null;
  } catch (error) {
    // Never let a poster failure surface — the gif itself is already uploaded.
    console.warn("GIF poster upload skipped:", error);
    return null;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

// ISP-9999 (gordon-0923-video-upload-11s-takes-very-long): measured live on
// web-dev — an 11s/1080p phone-sized clip (29.72 MB) PUT direct-to-S3 with the
// bare `fetch()` below took 160.94s on a throttled (1.5 Mbps) connection with
// ZERO visible progress the entire time (the caller only ever sees a binary
// "uploading" flag) and no way to cancel. `fetch()` cannot report upload
// progress (no `onprogress`-equivalent for a request body in this app's target
// browsers), so real progress + cancel requires XMLHttpRequest for the PUT.
//
// Only taken when a caller opts in (`onProgress` and/or `signal` passed) — no
// caller passes them yet, so every existing call site (VideoUpload, Capture,
// s3Helper.test.js's gif-poster suite) stays on the byte-identical `fetch()`
// path. See uploadFile.progress.test.js for the new behaviour.
export class UploadCancelledError extends Error {
  constructor(message = "Upload cancelled") {
    super(message);
    this.name = "UploadCancelledError";
    this.cancelled = true;
  }
}

const putFile = (url, file, headers, { onProgress, signal } = {}) => {
  if (!onProgress && !signal) {
    return fetch(url, { method: "PUT", body: file, mode: "cors", headers }).then(
      (r) => ({ ok: r.ok, status: r.status, statusText: r.statusText })
    );
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([key, value]) =>
      xhr.setRequestHeader(key, value)
    );
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        });
      };
    }
    xhr.onload = () =>
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, statusText: xhr.statusText });
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new UploadCancelledError());
    if (signal) {
      // T22.170: an XHR that was opened but never sent fires NO abort event
      // when aborted, so the old `xhr.abort(); return;` here left this promise
      // pending forever — the panel sat on "Uploading… 0%" after a Cancel
      // pressed while the presign request was still in flight.
      if (signal.aborted) {
        reject(new UploadCancelledError());
        return;
      }
      signal.addEventListener("abort", () => xhr.abort());
    }
    xhr.send(file);
  });
};

// T22.170: settle with UploadCancelledError the moment `signal` aborts, even
// while `promise` (the presign request) is still pending, so Cancel takes
// effect immediately instead of after the presign round-trip.
const untilAborted = (promise, signal) => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(new UploadCancelledError());
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new UploadCancelledError());
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
};

export const uploadFile = async (file, folderStructure, options = {}) => {
  const { onProgress, signal } = options;
  const fileKey = buildUniqueObjectKey(folderStructure, file.name);

  try {
    // Get pre-signed URL from backend
    const {
      presigned_url,
      object_url,
      content_type,
      poster_presigned_url,
      poster_object_url,
    } = await untilAborted(getPresignedUrl(fileKey, file.size), signal);

    // Upload file to S3 via pre-signed URL.
    // Send the SAME Content-Type the backend signed the URL with (it derives it
    // from the object_path extension). The presigned signature covers
    // Content-Type, so letting the browser default to the blob's own type can
    // mismatch the signature and make S3 reject the PUT (e.g. an audio/mp4
    // recording signed as video/mp4). Falling back to the file's type preserves
    // the previous behaviour when the backend doesn't return a content_type.
    const uploadResponse = await putFile(
      presigned_url,
      file,
      {
        "x-amz-acl": "public-read",
        ...(content_type ? { "Content-Type": content_type } : {}),
      },
      { onProgress, signal }
    );

    if (!uploadResponse.ok) {
      throw new Error(
        `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`
      );
    }

    // GIF poster (best-effort; backend only returns poster_presigned_url for a
    // .gif key when GIF_ANIMATE_ENABLED). Awaited so a consumer that persists the
    // returned `poster` gets the resolved URL, but a failure never blocks/throws.
    const poster = await uploadGifPoster(
      file,
      poster_presigned_url,
      poster_object_url
    );

    return { location: object_url, fileKey, fileType: file.type, poster };
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};
